import { Factory, Hourglass } from 'lucide-react';

import type { Industry } from '@/api/types';
import { SortHeader } from '@/ui/SortHeader';
import { IndustryRow, type IndustryRowActions } from './IndustryRow';
import type { Sort, SortKey } from './industryList';

function EmptyRow({ colSpan, noIndustries }: { colSpan: number; noIndustries: boolean }) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <div className="empty">
          <div className="e-icon"><Factory aria-hidden="true" /></div>
          {noIndustries ? (
            <>
              <h3>No industries yet</h3>
              <p>Ideas can still be submitted; research proposes industries, which wait for review here.</p>
            </>
          ) : (
            <>
              <h3>No industries match</h3>
              <p>Clear the search or the status filter to see the rest.</p>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export function IndustriesTable({
  sort,
  onSort,
  pageRows,
  noIndustries,
  actions,
}: {
  sort: Sort;
  onSort: (key: string) => void;
  pageRows: Industry[];
  noIndustries: boolean;
  actions: IndustryRowActions;
}) {
  const header = (label: string, key: SortKey, className?: string) => (
    <SortHeader label={label} sortKey={key} sort={sort.key} order={sort.order} onSort={onSort} className={className} />
  );

  return (
    <div className="table-scroll">
      <table className="data zebra compact">
        <thead>
          <tr>
            {header('Industry', 'name')}
            {header('Description', 'description')}
            {header('Ideas', 'idea_count', 'num')}
            {header('Status', 'status')}
            {actions.withActions && <th scope="col"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 ? (
            <EmptyRow colSpan={actions.withActions ? 5 : 4} noIndustries={noIndustries} />
          ) : (
            pageRows.map((industry) => <IndustryRow key={industry.id} industry={industry} actions={actions} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Counts the industries research proposed, and filters to them. */
export function PendingBanner({
  pendingCount,
  canReview,
  showingPending,
  onToggle,
}: {
  pendingCount: number;
  canReview: boolean;
  showingPending: boolean;
  onToggle: () => void;
}) {
  const one = pendingCount === 1;
  const reviewLabel = canReview ? 'Review them' : 'Show them';
  return (
    <div className={`alert ${canReview ? 'alert-warning' : 'alert-info'} form-alert`} role="status">
      <Hourglass aria-hidden="true" />
      <div className="a-body">
        <b>
          {pendingCount} {one ? 'industry' : 'industries'} proposed by research
        </b>{' '}
        {one ? 'is' : 'are'} waiting for review.
        {canReview && ' Approve, or reject by moving its ideas to an approved industry.'}
      </div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onToggle}>
        {showingPending ? 'Show all' : reviewLabel}
      </button>
    </div>
  );
}
